import AppConfig from 'utils/app-config';
import axios from 'axios';
import Vue from 'vue';
import SessionService from 'services/SessionService';

/*
 * This class implements common billing api requirements like authentication over axios http client.
 *
 * It requires both serverHost and authToken configured on AppConfig because they are used to configure the requests.
 *
 * See axios documentation for details about request configuration.
 */
class BillingHTTP {
  constructor() {
    axios.interceptors.response.use(
      (response) => (response),
      (error) => {
        if (error && error.response && error.response.status === 401 && AppConfig.get('standalone')) {
          SessionService.clear();
          Vue.prototype.$applicationRouter.push({ name: 'login' });
        }

        return Promise.reject(error);
      },
    );
  }

  getContent(path, params, config = {}) {
    return this.get(path, params, config)
      .then((response) => response.data);
  }

  get(path, params, config = {}) {
    return this.request(path, { ...config, params, method: 'get' });
  }

  postContent(path, data, config = {}) {
    return this.post(path, data, config)
      .then((response) => response.data);
  }

  post(path, data, config = {}) {
    return this.request(path, { ...config, data, method: 'post' });
  }

  putContent(path, data, config = {}) {
    return this.put(path, data, config)
      .then((response) => response.data);
  }

  put(path, data, config = {}) {
    return this.request(path, { ...config, data, method: 'put' });
  }

  request(path, config) {
    const requestOptions = {
      ...config,
      url: process.env.API_HOST + path,
      headers: {
        Authorization: `Bearer ${AppConfig.get('authToken')}`,
        Impersonation: !!AppConfig.get('impersonatingUserIdFromQb'),
      },
      withCredentials: true,
    };

    return axios(requestOptions);
  }

  downloadPdf(path, fileName) {
    return this.get(path, null, { responseType: 'blob' })
      .then(({ data }) => {
        const blob = new Blob([data], { type: 'application/pdf' });
        // prevent IE download attribute error
        if ('msSaveOrOpenBlob' in window.navigator) {
          return window.navigator.msSaveOrOpenBlob(blob, fileName);
        }
        return this.createLinkToDownload(blob, fileName);
      });
  }

  createLinkToDownload(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('type', 'hidden');
    link.setAttribute('download', fileName);
    link.setAttribute('href', url);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

export default new BillingHTTP();
